// SetBulkLog authenticates with the `TokenID` in the request body, which is what lets
// the unload-safe path use a headerless `sendBeacon`.
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { LegacyAuthSessionService } from '../../auth/legacy-auth-session.service';
import { LEGACY_JSON_HEADERS, legacyTimestamp } from '../../http/legacy-request.util';
import { BackendRoutingService } from '../backend-routing/backend-routing.service';

import type { BulkLogApiService } from './bulk-log-api.service';
import type { BulkLogBatch, LegacyBulkLogEntry, LegacyBulkLogRequest } from './bulk-log.model';

@Injectable({ providedIn: 'root' })
export class LegacyBulkLogApi implements BulkLogApiService {
  private readonly http = inject(HttpClient);
  private readonly routing = inject(BackendRoutingService);
  private readonly legacyAuth = inject(LegacyAuthSessionService);

  async flush(batch: BulkLogBatch): Promise<void> {
    const request = this.legacyRequest(batch);
    if (!request.TokenID) throw new Error('Cannot flush SetBulkLog without a legacy TokenID.');
    const response = await firstValueFrom(
      this.http.post(`${this.routing.baseUrl}/SetBulkLog`, JSON.stringify(request), {
        headers: LEGACY_JSON_HEADERS,
        responseType: 'text',
      }),
    );
    if (!isLegacyBulkLogSuccess(response)) {
      throw new Error('SetBulkLog did not return Status 200.');
    }
  }

  flushUnloadSafe(batch: BulkLogBatch): boolean {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function')
      return false;
    const request = this.legacyRequest(batch);
    if (!request.TokenID) return false;
    const payload = new Blob([JSON.stringify(request)], {
      type: 'application/json; charset=utf-8',
    });
    return navigator.sendBeacon(`${this.routing.baseUrl}/SetBulkLog`, payload);
  }

  private legacyRequest(batch: BulkLogBatch): LegacyBulkLogRequest {
    return {
      TokenID: this.legacyAuth.getAuthContext().tokenId ?? '',
      DeviceTimeStamp: legacyTimestamp(),
      CallerAppType: String(batch.callerAppType),
      LogDetails: JSON.stringify(
        batch.logDetails.map((entry): LegacyBulkLogEntry => ({
          VerbValue: entry.verbValue,
          ActivityID: entry.activityId,
          EventTimeStamp: entry.eventTimeStamp,
          CallerUserID: entry.callerUserId,
          EventType: entry.eventType,
          VerbID: entry.verbId,
          CallerClassID: entry.callerClassId,
          OtherKeysAndValues: entry.otherKeysAndValues,
        })),
      ),
    };
  }
}

function isLegacyBulkLogSuccess(response: string): boolean {
  try {
    const parsed = JSON.parse(response) as { readonly Status?: string | number | null };
    return String(parsed?.Status ?? '') === '200';
  } catch {
    return false;
  }
}
